import { useTranslation } from "react-i18next";
import { ModalScreenPension } from "../../general/ModalScreen"
import { OverlayHeader } from "../expenses-overlay/OverlayHeader";
import { useContext, useState } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { reverseFormatNumber, toPrice } from "../../../utils/parsers";
import { Button } from "../../general/Button";
import { SelectInput } from "../../general/form-inputs/SelectInput";
import { Tooltip } from '../../general/Tooltip';
import { TooltipIndicator } from "../../general/TooltipIndicator";
import { IconElements } from "../../../config/icons";
import { Account, PensionContext } from "../../../contexts/pension-robot/PensionContext";
import { AccountReturnVoid } from "../cocktail-filler-screen/SupplementSection";
import { getResultSavingsAccount } from "../../../utils/functions";
import { useGetPensionCalcData } from "../../../api/pension-robot/useGetPensionCalcData";
import { SUPPLEMENT_PER_MONTH_DIVIDER } from "../cocktail-filler-screen/Cocktail";
import { DateTime } from "luxon";
import { useGetInvestmentForecast } from "../../../api/pension-robot/useGetInvestmentForecast";
import { LoadingStateWrapper } from "../../general/LoadingStateWrapper";
import { riskProfiles } from "../../../config/constants";

export type SupplementQuestionType = 'currentValue' | 'periodicDeposit' | 'investmentStrategy' | 'dateAvailable';

export const SupplementGeneralModal: React.FC<{ 
    isShown: boolean,
    isPartnerSection: boolean, 
    account: Account,
    updateAccount: (account:Account) => void,
    onClose: () => void,
    onlyAvailableAtPensionDate?: boolean,
}> = ({ isShown, onClose, updateAccount, account, isPartnerSection, onlyAvailableAtPensionDate=false }) => {
    const { t } = useTranslation('pension');
    const { actions, parameters: {personalInfo} } = useContext(PensionContext);

    const [currentAccount, setCurrentAccount] = useState<Account>({...account});

    const nextButtonEnabled = (currentAccount: Account) => 
    formCompleted(currentAccount)
    && (currentAccount.type === 'investment' ? investmentForecast.isSuccess : true)
    && !!computationResultMonthly
    && !!computationResult

    const formCompleted = (currentAccount: Account) =>
    ( !!currentAccount.availableAt || onlyAvailableAtPensionDate)
    && currentAccount.currentAmount !== undefined
    && currentAccount.recurringPaymentAmount !== undefined
    && currentAccount.recurringPaymentInterval !== undefined

    const pensionCalcData = useGetPensionCalcData();
    const yearlyMarketRate = pensionCalcData.data?.marketRate ?? 0;
    // TODO: Get legalRetirementAge from api once implemented in backend, ROBO-571 
    //const legalRetirementAge = pensionCalcData.data?.legalRetirementAge ?? 0;
    const legalRetirementAge = 67;
    const retirementYear = DateTime.now().year + (legalRetirementAge - (personalInfo.self?.age ?? 0));
    const recurringPaymentDuration = currentAccount.availableAt ? Math.ceil(Math.abs(DateTime.now().diff(DateTime.fromISO(currentAccount.availableAt), 'months').months)) : undefined;
    const investmentForecast = useGetInvestmentForecast(
        {
            riskProfileId: Number.parseInt(currentAccount.riskProfile ?? '10') || 10,
            paymentSingleAmount: currentAccount.currentAmount,
            paymentRecurringAmount: currentAccount.recurringPaymentAmount,
            paymentRecurringInterval: currentAccount.recurringPaymentInterval === 'month' ? 'Month' : 'Year',
            investmentEndDate: (DateTime.now().plus({ years: Math.max(legalRetirementAge - (personalInfo.self?.age ?? 0), 0) })).toISODate(),
            paymentRecurringEndDate: currentAccount.availableAt ?? (DateTime.now().plus({ years: Math.max(legalRetirementAge - (personalInfo.self?.age ?? 0), 0) })).toISODate(),
            automaticRiskReduction: true,
            calculationMode: onlyAvailableAtPensionDate ? 'NewProduct' : 'ExistingProduct'
        },
        currentAccount.type === 'investment' && formCompleted(currentAccount)
    );
    

    const computationResult = currentAccount.type === 'saving' 
        ? getResultSavingsAccount(
            currentAccount.currentAmount ?? 0, 
            currentAccount.recurringPaymentAmount ?? 0, 
            currentAccount.recurringPaymentInterval ?? 'month', 
            personalInfo.self?.age ?? 0, 
            legalRetirementAge, 
            yearlyMarketRate, 
            recurringPaymentDuration
        )
        : investmentForecast.data?.forecastExpirationValues?.median 
            ? investmentForecast.data?.forecastExpirationValues?.median * 100
            : 0;
            
    const computationResultMonthly = computationResult / SUPPLEMENT_PER_MONTH_DIVIDER;

    const updateModalAccountData = (changeAccount: Account) => {
        setCurrentAccount(changeAccount);
    }

    const applyAccount = () => {
        updateAccount({
            ...currentAccount,
            predictedAmount: computationResult,
            predictedAmountMonthly: computationResultMonthly
        })
        actions.setParameters({});
        onClose();
    }

    return (
        <ModalScreenPension isShown={isShown} >
            <div className="bg-white md:mx-auto md:rounded-xl p-8">
                <OverlayHeader
                    title={isPartnerSection
                        ? t('pension-cocktail-fill-screen-supplement-partner-modal-title')
                        : t('pension-cocktail-fill-screen-supplement-modal-title')}
                    onClose={onClose}
                />
                <div className="flex flex-col gap-4 mt-8" >
                    <SupplementModalInputContainer 
                        title={t('pension-cocktail-fill-screen-supplement-modal-amount-label')}
                        inputType="currentValue"
                        currentAccount={currentAccount}
                        onChange={updateModalAccountData}
                        
                    />
                    <SupplementModalInputContainer
                        title={t('pension-cocktail-fill-screen-supplement-modal-periodic-deposit-label')}
                        inputType="periodicDeposit"
                        currentAccount={currentAccount}
                        onChange={updateModalAccountData}
                    />
                    {currentAccount.type === 'investment' && <SupplementModalInputContainer
                        title={t('pension-cocktail-fill-screen-supplement-modal-risk-profile-label')}
                        inputType="investmentStrategy"
                        currentAccount={currentAccount}
                        onChange={updateModalAccountData}
                        tooltip={{
                            title: t("pension-cocktail-fill-screen-supplement-modal-periodic-deposit-tooltip-title"),
                            text: t("pension-cocktail-fill-screen-supplement-modal-periodic-deposit-tooltip-text")
                        }}
                    />}
                    {!onlyAvailableAtPensionDate && <SupplementModalInputContainer
                        title={t('pension-cocktail-fill-screen-supplement-modal-date-label')}
                        inputType="dateAvailable"
                        currentAccount={currentAccount}
                        onChange={updateModalAccountData}
                    />}
                    <LoadingStateWrapper 
                        isLoading={investmentForecast.isLoading}
                        isError={investmentForecast.isError}
                        isIdle={investmentForecast.isIdle}
                        isFetching={investmentForecast.isFetching}
                    >
                    <div className="flex flex-col sm:flex-row border-t pt-5 flex-wrap gap-4 sm:items-end" >
                        <div className="flex flex-col gap-4">
                            <span className="w-40 md:w-56 font-bold">
                                {t("pension-cocktail-fill-screen-results-save-or-invest-modal-exp-returns", {year: retirementYear})}
                            </span>
                        </div>
                        <div className="flex flex-col gap-2 sm:gap-4">
                            <span className="w-40 md:w-56 font-bold">
                                {t("pension-cocktail-fill-screen-results-save-or-invest-modal-exp-total")}
                            </span>
                            <span className="w-40 md:w-56">
                                {toPrice(computationResult, {
                                    onlyFullEuros: true,
                                    withCurrencySymbol: true,
                                })}
                            </span>
                            
                        </div>
                        <div className="flex flex-col gap-2 sm:gap-4">
                            <span className="font-bold">
                                {t("pension-cocktail-fill-screen-results-save-or-invest-modal-exp-monthly")}
                            </span>
                            <span>
                                {toPrice(computationResultMonthly, {
                                    onlyFullEuros: true,
                                    withCurrencySymbol: true,
                                })}
                            </span>
                        </div>
                    </div>
                    </LoadingStateWrapper>
                    <div className="flex gap-2 sm:gap-4 flex-row flex-wrap justify-start mt-8">
                        <Button
                            className="w-30 sm:w-44 h-10"
                            tint="neutral-no-hover"
                            size="sm"
                            onClick={onClose}
                        >
                            {t('general-cancel')}
                        </Button>
                        <Button 
                            className={`w-30 sm:w-44 h-10 ${!nextButtonEnabled(currentAccount) && 'opacity-50'}`}
                            tint="green" 
                            size="sm"
                            disabled={!nextButtonEnabled(currentAccount)}
                            onClick={applyAccount}
                        >
                            {t('general-calculate')}
                        </Button>
                    </div>
                </div>
            </div>
        </ModalScreenPension>
    );
};

export const SupplementModalInputContainer: React.FC<{
    title: string, 
    inputType: SupplementQuestionType,
    currentAccount: Account,
    tooltip?: { title: string, text: string },
    onChange?: (account: Account) => void
}>
= ({ title, inputType, currentAccount,tooltip, onChange }) => {
    return (
        <div className="flex flex-row flex-wrap gap-2 sm:gap-4 items-center justify-start">
            <span className="w-40 md:w-56">{title}</span>
            {inputType === 'currentValue' && <SupplementModalCurrentValue currentAccount={currentAccount} onChange={onChange}/>}
            {inputType === 'periodicDeposit' && <SupplementModalPeriodicDepositValue currentAccount={currentAccount} onChange={onChange}/>}
            {inputType === 'periodicDeposit' && <SupplementModalPeriodicDepositPeriod currentAccount={currentAccount} onChange={onChange}/>}
            {inputType === 'investmentStrategy' && <SupplementModalInvestmentStrategy currentAccount={currentAccount} onChange={onChange}/>}
            {inputType === 'dateAvailable' && <SupplementModalDateAvailable currentAccount={currentAccount} onChange={onChange}/>}
            {!!tooltip && ( 
                <Tooltip
                    className="w-10"
                    tooltipId={tooltip.title}
                    tooltipTitle={tooltip.title}
                    tooltipText={tooltip.text}
                >
                    <TooltipIndicator>?</TooltipIndicator>
                </Tooltip>
            )}
        </div>
    );
};

const SupplementModalCurrentValue: React.FC<{
    currentAccount: Account,
    onChange?: AccountReturnVoid
}> = ({ currentAccount, onChange }) => {

    return (
        <CurrencyInput
            type="text"
            className="w-40 md:w-56 py-2 px-4 border border-gray-200 rounded-lg focus:outline-none"
            fixedDecimalLength={0}
            decimalsLimit={2}
            placeholder="€"
            intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
            allowDecimals={false}
            value={!!currentAccount.currentAmount ? ((currentAccount.currentAmount ?? 0) / 100) : undefined}
            onValueChange={(value = '') => {
                if (onChange){
                    onChange({...currentAccount, currentAmount: reverseFormatNumber(value, 'nl-NL') * 100});
                }
            }}
        />
    );
};

const SupplementModalPeriodicDepositValue: React.FC<{
    currentAccount: Account ,
    onChange?: AccountReturnVoid
}> = ({ currentAccount, onChange }) => {

    return (
        <CurrencyInput
            type="text"
            className="w-40 md:w-56 py-2 px-4 border border-gray-200 rounded-lg focus:outline-none"
            fixedDecimalLength={0}
            decimalsLimit={2}
            placeholder="€"
            intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
            allowDecimals={false}
            value={!!currentAccount.recurringPaymentAmount ? ((currentAccount.recurringPaymentAmount ?? 0) / 100) : undefined}
            onValueChange={(value = '') => {
                if (onChange){
                    onChange({...currentAccount,recurringPaymentAmount: reverseFormatNumber(value, 'nl-NL') * 100});
                }
            }}
        />
    );
};

const SupplementModalPeriodicDepositPeriod: React.FC<{
    currentAccount: Account,
    onChange?: AccountReturnVoid
}> = ({ currentAccount, onChange }) => {
    const { t } = useTranslation('pension');
    return (
        <SelectInput
            className="w-36"
            selected={currentAccount.recurringPaymentInterval}
            onChange={(value) => {
                if (onChange){
                    onChange({...currentAccount, recurringPaymentInterval: value as 'year' | 'month'})
                }
            }}
            values={[
                {
                    label: t('general-per-month'),
                    value: 'month',
                },
                {
                    label: t('general-per-year'),
                    value: 'year',
                },
            ]}
        />
    );
}

const SupplementModalInvestmentStrategy: React.FC<{
    currentAccount: Account,
    onChange?: AccountReturnVoid
}> = ({ currentAccount, onChange}) => {
    const { t } = useTranslation('pension');

    return (
        <SelectInput
            className="w-40 md:w-56"
            onChange={(value) => {
                if (onChange){
                    onChange({...currentAccount, riskProfile: value})
                }
            }}
            selected={currentAccount.riskProfile || riskProfiles.neutral}
            values={[
                {
                    label: t('general-very-defensive'),
                    value: riskProfiles.veryDefensive,
                },
                {
                    label: t('general-defensive'),
                    value: riskProfiles.defensive,
                },
                {
                    label: t('general-neutral'),
                    value: riskProfiles.neutral,
                },
                {
                    label: t('general-offensive'),
                    value: riskProfiles.offensive,
                },
                {
                    label: t('general-very-offensive'),
                    value: riskProfiles.veryOffensive,
                },
            ]}
        />
    );
};

const SupplementModalDateAvailable: React.FC<{
    currentAccount: Account,
    onChange?: AccountReturnVoid
}> = ({ currentAccount, onChange }) => {

    return (
        <div className="relative">
            <input
                className="text-black-2 w-full py-2 px-4 border rounded-lg focus:outline-none bg-white border-gray-200"
                placeholder="dd-mm-yyyy"
                type="Date"
                value={!!currentAccount.availableAt ? currentAccount.availableAt : ''}
                required
                onChange={(e) => {
                    if (onChange){
                        onChange({ ...currentAccount, availableAt: e.target.value });
                    }
                }}
            ></input>
            <IconElements.Calendar className="absolute top-2 right-4" />
        </div>
    );
};

